/**
 * 短信发送记录管理组件
 * 功能：查看短信验证码发送历史
 */
import React, { useState, useEffect, useCallback } from 'react'
import {
    DevicePhoneMobileIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// API Base URL
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.talknect.com'

interface SmsLog {
    id: number
    phone: string
    code: string
    msg_id: string
    platform_code: string
    ip_address: string
    purpose: string
    status: string
    verified_at: string | null
    expires_at: string
    created_at: string
}

interface Pagination {
    page: number
    limit: number
    total: number
    total_pages: number
}

// 获取授权头
const getAuthHeaders = () => {
    const token = localStorage.getItem('token')
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
}

export default function SmsLogsManagement() {
    const [logs, setLogs] = useState<SmsLog[]>([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0
    })
    const [phoneFilter, setPhoneFilter] = useState('')

    // 加载短信日志
    const loadLogs = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString()
            })
            if (phoneFilter) {
                params.set('phone', phoneFilter)
            }

            const response = await fetch(`${API_BASE}/api/v1/admin/sms-config/logs?${params}`, {
                headers: getAuthHeaders()
            })
            const data = await response.json()

            if (data.success) {
                setLogs(data.data || [])
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || 0,
                    total_pages: data.pagination?.total_pages || 0
                }))
            }
        } catch (error) {
            console.error('加载短信日志失败:', error)
            toast.error('加载短信日志失败')
        } finally {
            setLoading(false)
        }
    }, [pagination.page, pagination.limit, phoneFilter])

    useEffect(() => {
        loadLogs()
    }, [loadLogs])

    // 格式化时间
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-'
        const date = new Date(dateStr)
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    // 状态映射
    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'verified':
                return { label: '已验证', color: 'bg-green-100 text-green-700', icon: CheckCircleIcon }
            case 'expired':
                return { label: '已过期', color: 'bg-gray-100 text-gray-500', icon: ClockIcon }
            case 'pending':
                return { label: '待验证', color: 'bg-yellow-100 text-yellow-700', icon: ClockIcon }
            default:
                return { label: status, color: 'bg-gray-100 text-gray-500', icon: XCircleIcon }
        }
    }

    // 用途映射
    const getPurposeLabel = (purpose: string) => {
        switch (purpose) {
            case 'register':
                return '注册'
            case 'login':
                return '登录'
            case 'reset_password':
                return '重置密码'
            case 'test':
                return '测试'
            default:
                return purpose
        }
    }

    return (
        <div className="p-6">
            {/* 标题 */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <DevicePhoneMobileIcon className="w-8 h-8 text-cyan-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">短信发送记录</h1>
                        <p className="text-sm text-gray-500">查看所有短信验证码发送历史</p>
                    </div>
                </div>
            </div>

            {/* 搜索栏 */}
            <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="搜索手机号..."
                        value={phoneFilter}
                        onChange={(e) => setPhoneFilter(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && loadLogs()}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                </div>
                <button
                    onClick={loadLogs}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                >
                    搜索
                </button>
            </div>

            {/* 日志表格 */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                </div>
            ) : logs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    <DevicePhoneMobileIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>暂无短信发送记录</p>
                </div>
            ) : (
                <>
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">手机号</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">验证码</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">用途</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">平台</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">发送时间</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {logs.map((log) => {
                                    const statusInfo = getStatusLabel(log.status)
                                    return (
                                        <tr key={log.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{log.phone}</div>
                                                <div className="text-xs text-gray-500">IP: {log.ip_address || '-'}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className="text-sm bg-gray-100 px-2 py-1 rounded">{log.code || '-'}</code>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {getPurposeLabel(log.purpose)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                                                    <statusInfo.icon className="w-4 h-4" />
                                                    {statusInfo.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {log.platform_code}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {formatDate(log.created_at)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* 分页 */}
                    <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-gray-500">
                            共 {pagination.total} 条记录
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.page <= 1}
                                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="w-5 h-5" />
                            </button>
                            <span className="text-sm text-gray-600">
                                第 {pagination.page} / {pagination.total_pages || 1} 页
                            </span>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.page >= pagination.total_pages}
                                className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRightIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
